import { createSelector } from "reselect";

import { SaleType } from "constants/sale";

import {
  bestRelationsForSale,
  buildRelationshipDetails,
  summariseRelationships,
} from "lib/businessRelations";

import { getCurrentSale } from "./currentSale";
import {
  createLookupCombiner,
  createLookupSelectors,
  objectMapper,
} from "./lib";
import { selectBusinessRelationIdsByBusinessIdLookup } from "./indexes";
import { getBusinesses, getBusinessRelations } from "./root";

export const getBusinessRelationById = relationId => state =>
  getBusinessRelations(state)[relationId] || null;

export const [
  selectBusinessRelationsByBusinessIdLookup,
  getBusinessRelationsByBusinessId,
] = createLookupSelectors(
  [selectBusinessRelationIdsByBusinessIdLookup, getBusinessRelations],
  createLookupCombiner(objectMapper),
);

export interface VendorRelationshipsSummary {
  text: string;
  tooltip: string;
}

export const selectVendorRelationshipsSummaryByBusinessIdLookup =
  createSelector(
    [getCurrentSale, getBusinesses, selectBusinessRelationsByBusinessIdLookup],
    (
      sale: Sale | null,
      businesses: Record<string, Business>,
      businessRelationsByBusinessIdLookup: Record<string, BusinessRelation[]>,
    ): Record<string, VendorRelationshipsSummary> =>
      Object.entries(businessRelationsByBusinessIdLookup).reduce(
        (acc, [businessId, relations]) => {
          const applicableRelations = sale
            ? bestRelationsForSale(relations, sale)
            : relations;
          const texts = buildRelationshipDetails(
            applicableRelations,
            businesses,
            { listingFirst: sale?.sale_type === SaleType.CLEARING },
          ).map(detail => detail.text);
          if (texts.length > 0) {
            acc[businessId] = {
              text: summariseRelationships(texts),
              tooltip: texts.join("\n"),
            };
          }
          return acc;
        },
        {},
      ),
  );
